import React from 'react';
import {
  Search, Users, ImageIcon, FileText, Bell, Trash2,
  LockOpen, Copy, Download, X, Phone, Video,
  Info, Shield, Calendar
} from 'lucide-react';

interface ChatInfoPanelProps {
  roomName: string;
  roomAvatar?: string;
  description?: string;
  participantCount: number;
  isGroup: boolean;
  createdAt?: string;
  businessName?: string;
  businessLogo?: string;
  propertyName?: string;
  members?: Array<{
    id: string;
    full_name: string;
    avatar_url?: string;
    role?: 'admin' | 'member';
  }>;
  onClose?: () => void;
  onSearchInChat?: () => void;
  onViewMedia?: () => void;
  onLeaveGroup?: () => void;
  onMuteNotifications?: () => void;
  onShowMembers?: () => void;
  isEncrypted?: boolean;
  onCopyInviteLink?: () => void;
}

const ChatInfoPanel: React.FC<ChatInfoPanelProps> = ({
  roomName,
  roomAvatar,
  description,
  participantCount,
  isGroup,
  createdAt,
  businessName,
  businessLogo,
  propertyName,
  members = [],
  onClose,
  onSearchInChat,
  onViewMedia,
  onLeaveGroup,
  onMuteNotifications,
  onShowMembers,
  isEncrypted = false,
  onCopyInviteLink
}) => {
  return (
    <div className="flex flex-col h-full bg-light-card dark:bg-dark-card border-l border-light-border dark:border-dark-border overflow-y-auto">
      {/* Header */}
      <div className="p-4 border-b border-light-border dark:border-dark-border flex items-center justify-between shrink-0">
        <h3 className="font-semibold text-light-text dark:text-dark-text">Details</h3>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-subtle dark:text-dark-subtle"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6 flex-1">
        {/* Avatar and Name */}
        <div className="flex flex-col items-center text-center">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary/20 to-golden/20 dark:from-primary/30 dark:to-golden/30 flex items-center justify-center overflow-hidden mb-3">
            {roomAvatar ? (
              <img src={roomAvatar} alt={roomName} className="w-full h-full object-cover" />
            ) : (
              <span className="text-3xl font-bold text-primary dark:text-golden">
                {roomName.charAt(0).toUpperCase()}
              </span>
            )}
          </div>
          <h2 className="text-lg font-bold text-light-text dark:text-dark-text">
            {roomName}
          </h2>

          {description && (
            <p className="text-sm text-light-subtle dark:text-dark-subtle mt-1">
              {description}
            </p>
          )}

          {/* Encryption Info */}
          {isEncrypted && (
            <div className="flex items-center gap-1.5 mt-2 px-2 py-1 bg-green-100 dark:bg-green-900/20 rounded-full">
              <Shield className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                Encrypted
              </span>
            </div>
          )}
        </div>

        {/* Room Info */}
        <div className="space-y-3">
          <div>
            <p className="text-xs uppercase text-light-subtle dark:text-dark-subtle font-semibold mb-2">
              Information
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                <span className="text-light-text dark:text-dark-text">Members</span>
                <span className="font-semibold text-light-text dark:text-dark-text">
                  {participantCount}
                </span>
              </div>

              {businessName && (
                <div className="flex items-center gap-2 p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                  <span className="text-light-text dark:text-dark-text">Business</span>
                  <span className="font-semibold text-light-text dark:text-dark-text truncate">
                    {businessName}
                  </span>
                </div>
              )}

              {propertyName && (
                <div className="flex items-center gap-2 p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                  <span className="text-light-text dark:text-dark-text">Property</span>
                  <span className="font-semibold text-light-text dark:text-dark-text truncate">
                    {propertyName}
                  </span>
                </div>
              )}

              {createdAt && (
                <div className="flex items-center gap-2 p-2 bg-light-bg dark:bg-dark-bg rounded-lg">
                  <Calendar className="h-4 w-4 text-light-subtle dark:text-dark-subtle shrink-0" />
                  <span className="text-xs text-light-subtle dark:text-dark-subtle">
                    Created {new Date(createdAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-2">
          <p className="text-xs uppercase text-light-subtle dark:text-dark-subtle font-semibold mb-2">
            Actions
          </p>

          <button
            onClick={onSearchInChat}
            className="w-full p-3 flex items-center gap-3 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-text dark:text-dark-text"
          >
            <Search className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
            <span className="text-sm font-medium">Search in chat</span>
          </button>

          <button
            onClick={onViewMedia}
            className="w-full p-3 flex items-center gap-3 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-text dark:text-dark-text"
          >
            <ImageIcon className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
            <span className="text-sm font-medium">Media & files</span>
          </button>

          <button
            onClick={onShowMembers}
            className="w-full p-3 flex items-center gap-3 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-text dark:text-dark-text"
          >
            <Users className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
            <span className="text-sm font-medium">View members</span>
          </button>

          {isGroup && (
            <button
              onClick={onCopyInviteLink}
              className="w-full p-3 flex items-center gap-3 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-text dark:text-dark-text"
            >
              <Copy className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
              <span className="text-sm font-medium">Copy invite link</span>
            </button>
          )}
        </div>

        {/* Additional Actions */}
        <div className="space-y-2 border-t border-light-border dark:border-dark-border pt-4">
          <button
            onClick={onMuteNotifications}
            className="w-full p-3 flex items-center gap-3 bg-light-bg dark:bg-dark-bg rounded-lg hover:bg-light-border dark:hover:bg-dark-border transition text-light-text dark:text-dark-text"
          >
            <Bell className="h-4 w-4 text-light-subtle dark:text-dark-subtle" />
            <span className="text-sm font-medium">Mute notifications</span>
          </button>

          {isGroup && (
            <button
              onClick={onLeaveGroup}
              className="w-full p-3 flex items-center gap-3 bg-red-50 dark:bg-red-900/10 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 transition text-red-600 dark:text-red-400"
            >
              <Trash2 className="h-4 w-4" />
              <span className="text-sm font-medium">Leave group</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatInfoPanel;
